// @ts-check

import { useEventListener } from "@vueuse/core";
import { computed, onMounted, watch } from "vue";

import { MOUNT_READY_EVENT, MOUNT_READY_MARKER } from "#ce/framework/mount-ready";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { FINDER_FILTER_CONNECT_EVENT, FINDER_FILTER_STATE_EVENT } from "#ce/shared/finder-filter-protocol";

/**
 * The filter's half of the protocol its bar reads — the bar's half is `useFinderFilterRegistry`.
 * A filter announces itself to the bar and reports its state; the bar acts on it only through the
 * actions handed over here.
 *
 * Nothing is built and no listener is attached when `bar` is null, so a filter standing alone runs
 * no protocol code.
 *
 * @param {HTMLElement | null} bar - the enclosing `sp-finder-filter-bar`, resolved with `closest()`.
 * @param {object} filter
 * @param {import("vue").Ref<boolean>} filter.open - the filter's open model, which `open`/`close` write.
 * @param {() => import("#ce/shared/finder-filter-protocol").FinderFilterState} filter.readState
 * @param {() => Omit<import("#ce/shared/finder-filter-protocol").FinderFilterConnectPayload, "state" | "actions">} filter.describe
 * @param {() => void} filter.clear - clears the applied value.
 */
export function useFinderFilterAnnouncement(bar, { open, readState, describe, clear }) {
  if (!bar) {
    return;
  }

  const { emitBubbling } = useHostEmit();

  const state = computed(readState);

  /** @type {import("#ce/shared/finder-filter-protocol").FinderFilterActions} */
  const actions = Object.freeze({
    open() {
      open.value = true;
    },
    close() {
      open.value = false;
    },
    clear,
  });

  const announce = () => emitBubbling(FINDER_FILTER_CONNECT_EVENT, { ...describe(), state: state.value, actions });

  // A bar that is already mount-ready dispatched its event before this filter mounted, and one
  // that remounts dispatches it again.
  useEventListener(bar, MOUNT_READY_EVENT, announce);

  onMounted(() => {
    if (bar.hasAttribute(MOUNT_READY_MARKER)) {
      announce();
    }
  });

  watch(state, (snapshot) => emitBubbling(FINDER_FILTER_STATE_EVENT, snapshot), { flush: "post" });
}
